import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Building2, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";
import { PageHero } from "./Services";
import LeadForm from "../components/LeadForm";
import WarrantyBadge from "../components/WarrantyBadge";
import { Link } from "react-router-dom";
import { brand, faqs, inspection, warranty } from "../data/site";
import { serviceAreas } from "../data/serviceAreas";

const Contact = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div>
      <SEO
        title="Contact Us | Free Estimate | Pepla Roofing"
        description={`Free roof inspections and estimates across Phoenix and Maricopa County, seven days a week. Call ${brand.phone} or send the form.`}
      />
      <PageHero
        eyebrow="Get In Touch"
        title="Contact Pepla"
        subtitle="Ready for your next project? Reach out for a free, no-obligation estimate."
      />

      {/* Contact + Form */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            <div>
              <h2 className="font-heading text-3xl uppercase tracking-tight mb-2">Let's Talk</h2>
              <div className="brand-line mb-6" />
              <p className="text-muted-foreground leading-relaxed">
                Whether it's a leak, a full replacement or tile underlayment — tell us what's going on and we'll take a look. Free inspections, seven days a week.
              </p>
            </div>

            <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-4 group bg-secondary rounded-xl p-5 hover:bg-ink hover:text-white transition-colors">
              <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><Phone size={20} className="text-ink" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-white/60">Call Us</div>
                <div className="font-heading text-xl">{brand.phone}</div>
              </div>
            </a>

            <a href={`mailto:${brand.emailRaw || brand.email}`} className="flex items-center gap-4 group bg-secondary rounded-xl p-5 hover:bg-ink hover:text-white transition-colors">
              <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><Mail size={20} className="text-ink" /></span>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-muted-foreground group-hover:text-white/60">Email</div>
                <div className="font-medium break-words">{brand.email}</div>
              </div>
            </a>

            <div className="flex items-center gap-4 bg-secondary rounded-xl p-5">
              <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><MapPin size={20} className="text-ink" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Where We Work</div>
                <div className="font-medium text-sm">Phoenix and every city in Maricopa County</div>
                <Link to="/service-areas" className="text-xs text-brand-dark font-semibold mt-1.5 inline-block hover:underline">See all service areas</Link>
              </div>
            </div>

            {brand.address && (
              <div className="flex items-center gap-4 bg-secondary rounded-xl p-5">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><Building2 size={20} className="text-ink" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Office</div>
                  <div className="font-medium text-sm">{brand.address}</div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 bg-secondary rounded-xl p-5">
              <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center shrink-0"><Clock size={20} className="text-ink" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Inspections &amp; Estimates</div>
                <div className="font-medium text-sm">{inspection.hours}</div>
                {inspection.daylight && (
                  <div className="text-xs text-ink/60 mt-1.5 leading-relaxed">
                    {inspection.daylight}
                  </div>
                )}
                {brand.hours && (
                  <div className="text-xs text-muted-foreground mt-1.5">
                    {brand.hoursLabel}: {brand.hours}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 min-w-0">
            <div className="bg-card border border-ink/10 rounded-2xl p-6 md:p-8 shadow-sm">
              <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Request Your Free Estimate</h3>
              <p className="text-muted-foreground text-sm mb-6">Fill out the form and our team will respond promptly.</p>
              <LeadForm />
            </div>

            {/* The seal sits under the form rather than above it: the question
                it answers — "and what if they botch it?" — is the one someone
                asks on the way to pressing send, not on the way in. */}
            {warranty.laborYears && (
              <div className="mt-6 bg-ink text-white rounded-2xl p-6 flex items-center gap-5">
                <WarrantyBadge size={116} className="w-24 sm:w-28" />
                <div className="min-w-0">
                  <div className="font-heading text-xl uppercase tracking-wide leading-tight">
                    {warranty.laborYears}-Year Workmanship Warranty
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed mt-1.5">
                    On every roof we install — non-prorated, no deductible, and it transfers
                    once to the next owner for free.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <span className="kicker-dark">Answers</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Frequently Asked Questions</h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden bg-white/5">
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-heading text-lg uppercase tracking-wide">{f.q}</span>
                  <ChevronDown size={20} className={`text-brand shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-white/70 text-sm leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <p className="text-white/50 text-xs uppercase tracking-widest mb-4">Proudly Serving</p>
            <div className="flex flex-wrap justify-center gap-2">
              {serviceAreas.map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="text-xs border border-white/15 rounded-full px-3 py-1 text-white/70 hover:border-brand hover:text-brand transition-colors">{a.name}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
